import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { ModalFilterPlanByCalendar } from './ModalFilterPlanByCalendar'
import { IconCalendar, IconHuman } from './Icon'
import { DataSearchType, searchData, KeyDataSearch } from './type'
import { DatePicker } from '../../date-picker'
import { DataType } from '../../date-picker/types'
import { stayDayOptions } from '../../search-bar/utils'

type Props = {
  onChange?: () => void
}

export const SearchCalendar = (props: Props) => {
  const [openModal, setOpenModal] = useState(false)
  const [filterData, setFilterData] = useState<DataSearchType>(searchData)
  const [checkInDates, setCheckInDates] = useState<DataType[]>([])
  const { t } = useTranslation()

  useEffect(() => {
    const checkInDatesData = (document.querySelector('[data-check-in-dates]') as HTMLElement)?.dataset?.checkInDates || '[]'
    setCheckInDates(JSON.parse(checkInDatesData))
  }, [])

  const fromDateText = useMemo(() => {
    if (filterData.start_date) {
      return dayjs(filterData.start_date).format('MM/DD')
    }
    const urlParams = new URLSearchParams(window.location.search)
    const selectedMonth = (urlParams.get('select_month') || '').split(',').sort()[0]?.replace('/', '-') // "2025/08,2025/06,2025/10"
    if (selectedMonth) {
      const selectedDate = checkInDates.find((date) => date.date.startsWith(selectedMonth))
      if (selectedDate) {
        return dayjs(selectedDate.date).format('MM/DD')
      }
    }
    if (checkInDates[0]) {
      return dayjs(checkInDates[0].date).format('MM/DD')
    }
    return ''
  }, [filterData.start_date, checkInDates])

  const renderSearchContent = () => {
    const stayDay = filterData.stay_day
    const STAY_DAY_OPTIONS = useMemo(() => stayDayOptions(t), [])
    const stayDayText = STAY_DAY_OPTIONS.find((option) => option.value === stayDay)?.name

    return (
      <div className="flex items-center justify-start gap-1 overflow-hidden">
        <IconCalendar />
        <div className="whitespace-nowrap font-size-14">
          {fromDateText} ~ {stayDayText}
        </div>
        <div>|</div>
        <IconHuman />
        <div>
          {t('plan_detail.reservation.modal.people.adult')}
          {filterData.number_of_adults}
          {t('reservation_confirm.modal_booking.people')} {t('plan_detail.reservation.modal.people.child')}
          {filterData.number_of_preschoolers +
            filterData.number_of_students +
            filterData.number_of_preschoolers_1 +
            filterData.number_of_students_1}
          {t('reservation_confirm.modal_booking.people')}
        </div>
      </div>
    )
  }

  useEffect(() => {
    if (open) {
      const queryParams = new URLSearchParams(window.location.search)
      const tempObject: DataSearchType = { ...searchData }
      const arrayKeys = ['stay_day']
      const numberKeys = [
        'number_of_adults',
        'number_of_preschoolers',
        'number_of_students',
        'number_of_preschoolers_1',
        'number_of_students_1',
      ]
      const keySearch = Object.keys(searchData)

      queryParams.forEach((value, key) => {
        if (keySearch.includes(key)) {
          if (arrayKeys.includes(key)) {
            tempObject[key] = value ? value : ''
          } else if (numberKeys.includes(key)) {
            tempObject[key] = Number(value)
          } else {
            tempObject[key] = value
          }
        }
      })
      setFilterData(tempObject)
    }
  }, [open, window])

  const handleGenerateSearchQueryURL = (currentKeySearch: string, data: DataSearchType) => {
    const query = {
      number_of_adults: String(data.number_of_adults),
      number_of_preschoolers: String(data.number_of_preschoolers),
      number_of_students: String(data.number_of_students),
      number_of_preschoolers_1: String(data.number_of_preschoolers_1),
      number_of_students_1: String(data.number_of_students_1),
      stay_day: data.stay_day,
      start_date: data.start_date ? dayjs(data.start_date).format('YYYY-MM-DD') : '',
    }
    const currentQueryParams = new URLSearchParams(currentKeySearch)
    Object.keys(query).forEach((key) => {
      if (query[key] && !['false'].includes(query[key])) {
        currentQueryParams.set(key, query[key])
      } else {
        currentQueryParams.delete(key)
      }
    })
    if (currentQueryParams.get('page')) {
      currentQueryParams.set('page', '1')
    }
    return currentQueryParams.toString()
  }

  const handleChangeDataSearch = (data: DataSearchType, checkInDates: DataType[]) => {
    setFilterData(data)
    setCheckInDates(checkInDates)
    setOpenModal(false)
    const currentKeySearch = window.location.search
    const queryURL = handleGenerateSearchQueryURL(currentKeySearch, data)
    redirectToAvailabilityWithQuery(queryURL)
  }

  const handleChangeFromDate = (key: KeyDataSearch, value: number | string, type?: 'down' | 'up' | 'option' | 'text') => {
    const selectedDate = value ? dayjs(value).format('YYYY-MM-DD') : undefined
    const newStartDate = selectedDate === filterData.start_date ? '' : selectedDate
    setFilterData((prevDataSearch) => ({ ...prevDataSearch, start_date: newStartDate }))

    const currentKeySearch = window.location.search
    const queryURL = handleGenerateSearchQueryURL(currentKeySearch, {
      ...filterData,
      start_date: newStartDate,
    })
    redirectToAvailabilityWithQuery(queryURL)
  }

  const redirectToAvailabilityWithQuery = (queryURL: string) => {
    const pathname = window.location.pathname
    const isAvailabilityPage = pathname.endsWith('/availability')

    if (isAvailabilityPage) {
      history.pushState({}, '', queryURL ? `?${queryURL}` : pathname)
      props.onChange?.()
    } else {
      const newPath = pathname.replace(
        /(\/reviews|\/privileges|\/study_facilities|\/local_attractions|\/coworking|\/access)?$/,
        '/availability',
      )
      const finalURL = `${newPath}${queryURL ? `?${queryURL}` : ''}`
      window.location.href = finalURL
    }
  }

  return (
    <div
      style={{
        width: '100%',
        padding: '25px 15px',
      }}
    >
      <button onClick={() => setOpenModal(true)} className="button-search-calendar flex items-center rounded-8 mb-2">
        {renderSearchContent()}
      </button>
      <div className="mt-2">
        <DatePicker
          data={checkInDates}
          onChangeDataSearch={handleChangeFromDate}
          initDate={filterData.start_date ? new Date(filterData.start_date) : undefined}
          range={Number(filterData.stay_day)}
        />
      </div>
      {openModal && (
        <ModalFilterPlanByCalendar
          open={openModal}
          onClose={() => setOpenModal(false)}
          initDataSearch={filterData}
          onChangeDataSearch={handleChangeDataSearch}
        />
      )}
    </div>
  )
}
